import { defineStore } from "pinia";
import { computed, ref } from "vue";

interface User {
  id: string;
  email: string;
  displayName: string;
  roles: ReadonlyArray<string>;
  permissions: ReadonlyArray<string>;
  /**
   * Where the credentials live. "LDAP" / "SSO" users see fewer
   * actions in the GUI (no password reset, no edit-profile) — that
   * surface is owned by FreeIPA / the IdP.
   */
  authProvider?: "LOCAL" | "LDAP" | "SSO";
}

interface LoginResponse {
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken: string;
  user: { id: string; email: string; displayName: string };
}

const TOKEN_KEY = "web1-crm:tokens";

interface PersistedTokens {
  accessToken: string;
  refreshToken: string;
}

function readPersisted(): PersistedTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedTokens;
  } catch {
    return null;
  }
}

function writePersisted(tokens: PersistedTokens | null): void {
  if (tokens) {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export const useAuthStore = defineStore("auth", () => {
  const tokens = ref<PersistedTokens | null>(readPersisted());
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => Boolean(tokens.value?.accessToken));

  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch("/api/v1/auth/login", {
        // GUI calls all API endpoints under /api/v1/...; the NestJS
        // global prefix in apps/api/src/main.ts maps controller paths
        // accordingly.
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(body.message ?? `Login failed (${res.status})`);
      }
      const data = (await res.json()) as LoginResponse;
      tokens.value = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      };
      writePersisted(tokens.value);
      await fetchMe();
    } catch (err) {
      error.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMe(): Promise<void> {
    if (!tokens.value) return;
    // Snapshot the token at call-time so a logout/change-password
    // racing with an in-flight /me request can't repopulate `user`
    // after the session was deliberately cleared.
    const tokenAtStart = tokens.value.accessToken;
    try {
      const res = await fetch("/api/v1/auth/me", {
        headers: { Authorization: `Bearer ${tokenAtStart}` },
      });
      // If the token changed (or was cleared) while we were waiting
      // for /me to respond, drop the result on the floor — the
      // session in scope when we fired the request is no longer
      // the current one.
      if (tokens.value?.accessToken !== tokenAtStart) return;
      if (res.ok) {
        user.value = (await res.json()) as User;
      } else if (res.status === 401) {
        logout();
      }
    } catch {
      // Network blip during init — leave `user` null. The next
      // authFetch will trigger handleUnauthenticated on 401 if the
      // token has actually expired; if it was a transient error,
      // the user can keep working off whatever cached data is in
      // memory and the next interaction will refetch.
    }
  }

  function logout(): void {
    tokens.value = null;
    user.value = null;
    writePersisted(null);
  }

  /**
   * Self-service: trigger an email-link reset. The API always returns
   * 204 (it doesn't reveal whether the email exists), so this resolves
   * with `void` on any non-network failure too — surfacing API-shaped
   * errors here would defeat the API's anti-enumeration design.
   */
  async function forgotPassword(email: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await fetch("/api/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } finally {
      loading.value = false;
    }
  }

  /**
   * Redeem a PASSWORD_RESET token. Surfaces API errors (token expired,
   * password policy) so the view can show them inline.
   */
  async function resetPassword(
    token: string,
    newPassword: string,
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch("/api/v1/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        const message = body.message ?? `Reset failed (${res.status})`;
        throw new Error(message);
      }
    } catch (err) {
      error.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logged-in self-service password change. On success, the API has
   * revoked every refresh token for this user — we mirror that locally
   * by clearing in-memory tokens so the next route guard kicks the
   * user back to /login. Surfaces API errors (wrong current password,
   * policy, history) so the view can show them inline.
   */
  async function changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    if (!tokens.value) {
      throw new Error("Not authenticated");
    }
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch("/api/v1/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens.value.accessToken}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        const message = body.message ?? `Change failed (${res.status})`;
        throw new Error(message);
      }
      // API revoked every refresh token; mirror locally so the next
      // /me / refresh attempt kicks the user back to /login.
      logout();
    } catch (err) {
      error.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    tokens,
    user,
    loading,
    error,
    isAuthenticated,
    login,
    fetchMe,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
  };
});
